/**
 * The arithmetic behind the chart primitives: linear scales, readable tick steps, band
 * positions, and the plot box an axis leaves for the marks. No DOM, so the axis maths is
 * testable without a browser.
 */

/** A linear scale from a data range onto a pixel range. */
export function linearScale({ domain, range }) {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  const scale = (value) => (span === 0 ? r0 : r0 + ((value - d0) / span) * (r1 - r0));
  scale.domain = [d0, d1];
  scale.range = [r0, r1];
  scale.invert = (px) => (r1 === r0 ? d0 : d0 + ((px - r0) / (r1 - r0)) * span);
  return scale;
}

/**
 * A band scale for categorical positions (weeks along an axis, rows in a matrix).
 * `padding` is the share of a step left as the gap between bands (0.2 = 20%).
 */
export function bandScale({ domain, range, padding = 0.2 }) {
  const [r0, r1] = range;
  const n = Math.max(1, domain.length);
  const step = (r1 - r0) / n;
  const width = step * (1 - padding);
  const index = new Map(domain.map((v, i) => [v, i]));
  const scale = (value) => {
    const i = index.get(value);
    return i === undefined ? null : r0 + i * step + (step - width) / 2;
  };
  scale.bandwidth = () => width;
  scale.step = () => step;
  scale.domain = domain;
  scale.range = [r0, r1];
  scale.centre = (value) => {
    const x = scale(value);
    return x === null ? null : x + width / 2;
  };
  return scale;
}

/** The 1 / 2 / 5 × 10ⁿ step at or above a raw step, so ticks land on numbers people say. */
export function niceStep(raw) {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalized = raw / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return step * magnitude;
}

/**
 * Ticks for a value axis: a nice step, the domain rounded out to it, and the values between.
 * Hours are the only value axis this app has (PRD 7.3: one value axis per chart), so the domain
 * always starts at zero unless the caller says otherwise.
 * @param {[number, number]} domain
 * @param {{ count?: number, zero?: boolean }} [opts]
 */
export function ticks(domain, { count = 5, zero = true } = {}) {
  let [d0, d1] = domain;
  if (zero) d0 = Math.min(0, d0);
  if (!Number.isFinite(d0) || !Number.isFinite(d1) || d1 <= d0) {
    return { domain: [d0 || 0, (d0 || 0) + 1], step: 1, values: [d0 || 0, (d0 || 0) + 1] };
  }
  const step = niceStep((d1 - d0) / Math.max(1, count));
  const start = Math.floor(d0 / step) * step;
  const end = Math.ceil(d1 / step) * step;
  const values = [];
  // Count the steps rather than accumulating, so floating point does not drift the last tick.
  const steps = Math.round((end - start) / step);
  for (let i = 0; i <= steps; i += 1) values.push(Number((start + i * step).toFixed(10)));
  return { domain: [start, end], step, values };
}

/** The plot box left inside `width`×`height` once the axes have their margins. */
export function plotBox({ width, height, margin }) {
  const m = { top: 12, right: 12, bottom: 28, left: 48, ...margin };
  return {
    margin: m,
    left: m.left,
    top: m.top,
    width: Math.max(0, width - m.left - m.right),
    height: Math.max(0, height - m.top - m.bottom),
    right: width - m.right,
    bottom: height - m.bottom,
  };
}

/** Running totals for a stack, in layer order: each layer's [start, end] on the value axis. */
export function stack(values) {
  let base = 0;
  return values.map((v) => {
    const value = Number.isFinite(v) ? v : 0;
    const segment = [base, base + value];
    base += value;
    return segment;
  });
}
